import { useState, useEffect, useContext } from "react";
import apiClient from "../../services/apiClient";
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  Space,
  Modal,
  Popconfirm,
  Typography,
  Card,
  message,
  Tree,
  Row,
  Col,
  Tag,
  Tooltip,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  FileOutlined,
  ApartmentOutlined,
  TableOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminDashboard.css";
import "./SectorIndustryClassificationManagement.css";

const { Title, Text } = Typography;
const { Option } = Select;

const SectorIndustryClassificationManagement = () => {
  const { isDark } = useContext(ThemeContext);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [hierarchyStructure, setHierarchyStructure] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [addLevel, setAddLevel] = useState(null); // 'sector', 'industry', 'basic'
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("table");

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  useEffect(() => {
    fetchHierarchyData();
    fetchHierarchyStructure();
  }, []);

  const fetchHierarchyData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/sector-industry-classification`);
      setHierarchyData(response.data);
    } catch (error) {
      message.error("Failed to fetch hierarchy data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchyStructure = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/sector-industry-classification/structure`);
      setHierarchyStructure(response.data);
    } catch (error) {
      console.error("Failed to fetch hierarchy structure", error);
    }
  };

  const handleAddLevel = (level) => {
    setEditingItem(null);
    setParentData(null);
    setAddLevel(level);
    setSelectedMacro(null);
    setSelectedSector(null);
    setSelectedIndustry(null);
    form.resetFields();
    setModalVisible(true);
  };
  
  const handleAdd = (parent = null) => {
    setEditingItem(null);
    setParentData(parent);
    setAddLevel(null);
    form.resetFields();
    
    // If adding child, pre-fill parent fields
    if (parent) {
      // Fields are now direct properties, not nested in id object
      const macro = parent.macroeconomicSector;
      const sector = parent.sector;
      const industry = parent.industry;
      
      setSelectedMacro(macro);
      setSelectedSector(sector);
      setSelectedIndustry(industry);
      form.setFieldsValue({
        macroeconomicSector: macro,
        sector: sector,
        industry: industry,
      });
    }
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    setParentData(null);
    setAddLevel(null);
    // Fields are now direct properties, not nested in id object
    setSelectedMacro(record.macroeconomicSector);
    setSelectedSector(record.sector);
    setSelectedIndustry(record.industry);
    form.setFieldsValue({
      macroeconomicSector: record.macroeconomicSector,
      sector: record.sector,
      industry: record.industry,
      basicIndustry: record.basicIndustry,
      description: record.description,
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      // Use the numeric ID directly
      const classificationId = record.id; // This is now a number, not an object
      
      await apiClient.delete(
        `${baseUrl}/sector-industry-classification/${classificationId}`
      );
      message.success("Deleted successfully");
      fetchHierarchyData();
      fetchHierarchyStructure(); // Refresh structure for dropdowns
    } catch (error) {
      message.error("Failed to delete");
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Auto-fill empty fields: if industry empty, use sector; if basic empty, use industry
      const payload = {
        macroeconomicSector: values.macroeconomicSector,
        sector: values.sector,
        industry: values.industry || values.sector, // Use sector if industry is empty
        basicIndustry: values.basicIndustry || values.industry || values.sector, // Use industry or sector if basic is empty
        description: values.description,
      };

      if (editingItem) {
        // Use the numeric ID directly
        const classificationId = editingItem.id; // This is now a number, not an object
        
        await apiClient.put(
          `${baseUrl}/sector-industry-classification/${classificationId}`,
          payload
        );
        message.success("Updated successfully");
      } else {
        await apiClient.post(`${baseUrl}/sector-industry-classification`, payload);
        message.success(
          addLevel === 'macro' ? "Macroeconomic Sector added successfully" :
          addLevel === 'sector' ? "Sector added successfully" :
          addLevel === 'industry' ? "Industry added successfully" :
          addLevel === 'basic' ? "Basic Industry added successfully" :
          "Added successfully"
        );
      }
      setModalVisible(false);
      setParentData(null);
      setAddLevel(null);
      setSelectedMacro(null);
      setSelectedSector(null);
      setSelectedIndustry(null);
      form.resetFields();
      fetchHierarchyData();
      fetchHierarchyStructure(); // Refresh structure for dropdowns
    } catch (error) {
      message.error("Failed to save");
      console.error(error);
    }
  };

  const buildTreeData = () => {
    const tree = {};
    
    hierarchyData.forEach(item => {
      // Fields are now direct properties, not nested in id object
      const macro = item.macroeconomicSector;
      const sector = item.sector;
      const industry = item.industry;
      const basicIndustry = item.basicIndustry;
      
      if (!tree[macro]) {
        tree[macro] = {};
      }
      if (sector && !tree[macro][sector]) {
        tree[macro][sector] = {};
      }
      if (industry) {
        if (!tree[macro][sector][industry]) {
          tree[macro][sector][industry] = [];
        }
        if (basicIndustry) {
          tree[macro][sector][industry].push(basicIndustry);
        }
      }
    });

    return Object.keys(tree).map((macro, idx) => ({
      title: (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
              <FolderOutlined style={{ marginRight: 8, color: "#1890ff" }} />
              <strong>{macro}</strong>
            </span>
          
          </span>
        ),
        key: `macro-${idx}`,
        children: Object.keys(tree[macro]).map((sector, sIdx) => ({
          title: (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>
                <FolderOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                {sector}
              </span>
             
            </span>
          ),
          key: `sector-${idx}-${sIdx}`,
          children: Object.keys(tree[macro][sector]).map((industry, iIdx) => ({
            title: (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>
                  <FolderOutlined style={{ marginRight: 8, color: "#faad14" }} />
                  {industry}
                </span>
             
              </span>
            ),
            key: `industry-${idx}-${sIdx}-${iIdx}`,
            children: tree[macro][sector][industry].map((basic, bIdx) => ({
              title: (
                <span>
                  <FileOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                  {basic}
                </span>
              ),
              key: `basic-${idx}-${sIdx}-${iIdx}-${bIdx}`,
            })),
          })),
        })),
      }));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Tag color="cyan">{id}</Tag>,
    },
    {
      title: "Macroeconomic Sector",
      dataIndex: "macroeconomicSector",
      key: "macroeconomicSector",
      render: (text, record) => {
        const macro = record.macroeconomicSector;
        return <Tag color="blue">{macro}</Tag>;
      },
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      render: (text, record) => {
        const sector = record.sector;
        return sector ? <Tag color="green">{sector}</Tag> : <Tag color="default">-</Tag>;
      },
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
      render: (text, record) => {
        const industry = record.industry;
        return industry ? <Tag color="orange">{industry}</Tag> : <Tag color="default">-</Tag>;
      },
    },
    {
      title: "Basic Industry",
      dataIndex: "basicIndustry",
      key: "basicIndustry",
      render: (text, record) => {
        const basicIndustry = record.basicIndustry;
        return basicIndustry ? <Tag color="purple">{basicIndustry}</Tag> : <Tag color="default">-</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure to delete?"
              onConfirm={() => handleDelete(record)}
            >
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Get cascading options from hierarchyStructure (like IssuerList)
  const [selectedMacro, setSelectedMacro] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const getSectorOptions = (macro) => {
    if (!macro || !hierarchyStructure[macro]) return [];
    return Object.keys(hierarchyStructure[macro]).sort();
  };

  const getIndustryOptions = (macro, sector) => {
    if (!macro || !sector || !hierarchyStructure[macro]?.[sector]) return [];
    return Object.keys(hierarchyStructure[macro][sector]).sort();
  };

  const getBasicIndustryOptions = (macro, sector, industry) => {
    if (!macro || !sector || !industry || !hierarchyStructure[macro]?.[sector]?.[industry]) return [];
    return hierarchyStructure[macro][sector][industry].sort();
  };


  return (
    <div className={`admin-dashboard-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="admin-users-hero"
        style={{
          background: isDark 
            ? 'linear-gradient(135deg, rgba(255, 255, 240, 0.15) 0%, rgba(230, 230, 255, 0.2) 50%, rgba(255, 230, 240, 0.15) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 240, 0.4) 0%, rgba(230, 230, 255, 0.5) 50%, rgba(255, 230, 240, 0.4) 100%)',
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          marginBottom: 32,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.08)",
          position: 'relative',
          overflow: 'hidden',
          minHeight: 180,
          border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 42,
              color: isDark ? "#ffffff" : "#1a1a1a",
              textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            }}
          >
            Sector & Industry Classification
          </Title>
          <Text 
            style={{ 
              fontSize: 20, 
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
              textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            Centrally manage and organize your sector and industry classifications.
            Add, edit, or remove macroeconomic sectors, sectors, industries, and
            basic industries. Changes reflect across the entire platform instantly.
          </Text>
        </div>
      </div>
      <Divider
        style={{
          margin: 0,
          marginBottom: 32,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />

      {/* Action Buttons Row */}
      <div style={{ marginBottom: "24px" }}>
        <Space size="middle" wrap style={{ width: "100%", justifyContent: "center" }}>
          <Button
            type="default"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => handleAddLevel('macro')}
            style={{
              width: "253px",
              height: "83px",
              borderRadius: "20px",
              background: "#419DD229",
              borderWidth: "1px",
              borderColor: "#419DD2",
              color: isDark ? "#ffffff" : "#333",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Add Macro Sector
          </Button>
          <Button
            type="default"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => handleAddLevel('sector')}
            style={{
              width: "253px",
              height: "83px",
              borderRadius: "20px",
              background: "#419DD229",
              borderWidth: "1px",
              borderColor: "#419DD2",
              color: isDark ? "#ffffff" : "#333",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Add Sector
          </Button>
          <Button
            type="default"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => handleAddLevel('industry')}
            style={{
              width: "253px",
              height: "83px",
              borderRadius: "20px",
              background: "#419DD229",
              borderWidth: "1px",
              borderColor: "#419DD2",
              color: isDark ? "#ffffff" : "#333",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Add Industry
          </Button>
          <Button
            type="default"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => handleAddLevel('basic')}
            style={{
              width: "253px",
              height: "83px",
              borderRadius: "20px",
              background: "#419DD229",
              borderWidth: "1px",
              borderColor: "#419DD2",
              color: isDark ? "#ffffff" : "#333",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Add Basic Industry
          </Button>
        </Space>
      </div>

      {/* Main Content with Tabs */}
      <Card 
        className="sector-classification-card"
        style={{ 
          borderRadius: 16,
          boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: isDark ? "1px solid #3a3a4e" : "1px solid #f0f0f0",
          background: "transparent",
          transition: "none"
        }}
      >
        {/* Tabs Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: isDark ? "1px solid #434343" : "1px solid #f0f0f0"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, backgroundColor: isDark ? '#3a3a4e' : '#f5f5f5', borderRadius: 12, padding: 4 }}>
            <div
              onClick={() => setActiveTab("table")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "table" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <TableOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Table View</span>
            </div>
            
            <div
              onClick={() => setActiveTab("tree")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "tree" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <ApartmentOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Tree View</span>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "table" && (
          <div style={{ marginTop: 16 }}>
            <Table
              columns={columns}
              dataSource={hierarchyData}
              rowKey={(record) => record.id} // Use numeric ID as key
              loading={loading}
              pagination={{ pageSize: 10 }}
              style={{
                background: "transparent",
              }}
            />
          </div>
        )}
        
        {activeTab === "tree" && (
          <Card
            style={{
              background: "transparent",
              borderRadius: 8,
              marginTop: 16,
              border: "none",
              boxShadow: "none",
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Tree
              showLine
              defaultExpandAll
              treeData={buildTreeData()}
              style={{
                background: "transparent",
                fontSize: "14px",
              }}
            />
          </Card>
        )}
      </Card>

      <Modal
        title={
          editingItem 
            ? "Edit Hierarchy Item" 
            : addLevel === 'macro'
            ? "Add New Macroeconomic Sector"
            : addLevel === 'sector'
            ? "Add New Sector"
            : addLevel === 'industry'
            ? "Add New Industry"
            : addLevel === 'basic'
            ? "Add New Basic Industry"
            : parentData 
            ? "Add Child to: " + (parentData.basicIndustry || 
                                  parentData.industry || 
                                  parentData.sector || 
                                  parentData.macroeconomicSector)
            : "Add New Hierarchy Item"
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setParentData(null);
          setAddLevel(null);
          setSelectedMacro(null);
          setSelectedSector(null);
          setSelectedIndustry(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.macroeconomicSector !== undefined) {
              setSelectedMacro(changedValues.macroeconomicSector);
              // Clear dependent fields when macro changes (only for dropdowns)
              if (addLevel !== 'macro' && addLevel !== 'sector' && addLevel !== null) {
                form.setFieldsValue({ sector: undefined, industry: undefined, basicIndustry: undefined });
                setSelectedSector(null);
                setSelectedIndustry(null);
              }
            }
            if (changedValues.sector !== undefined) {
              setSelectedSector(changedValues.sector);
              // Clear dependent fields when sector changes (only for dropdowns)
              if (addLevel === 'basic') {
                form.setFieldsValue({ industry: undefined, basicIndustry: undefined });
                setSelectedIndustry(null);
              }
            }
            if (changedValues.industry !== undefined) {
              setSelectedIndustry(changedValues.industry);
              // Clear dependent fields when industry changes (only for dropdowns)
              if (addLevel === 'basic') {
                form.setFieldsValue({ basicIndustry: undefined });
              }
            }
          }}
        >
          <Form.Item
            name="macroeconomicSector"
            label="Macroeconomic Sector"
            rules={[{ required: true, message: "Macroeconomic Sector Required" }]}
          >
            {(addLevel === 'macro' || !addLevel) ? (
              <Input 
                placeholder="Enter Macroeconomic Sector"
                disabled={parentData && parentData.macroeconomicSector}
                style={parentData && parentData.macroeconomicSector ? { background: "#f5f5f5" } : {}}
              />
            ) : (
              <Select
                placeholder="Select Macroeconomic Sector"
                showSearch
                allowClear
                disabled={parentData && parentData.macroeconomicSector}
                style={parentData && parentData.macroeconomicSector ? { background: "#f5f5f5" } : {}}
              >
                {Object.keys(hierarchyStructure).sort().map((val) => (
                  <Option key={val} value={val}>{val}</Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item 
            name="sector" 
            label="Sector"
            rules={[{ required: true, message: "Sector Required" }]}
          >
            {(addLevel === 'macro' || addLevel === 'sector' || !addLevel) ? (
              <Input 
                placeholder="Enter Sector"
                disabled={parentData && parentData.sector}
                style={parentData && parentData.sector ? { background: "#f5f5f5" } : {}}
              />
            ) : (
              <Select
                placeholder="Select Sector"
                showSearch
                allowClear
                disabled={parentData && parentData.sector}
                style={parentData && parentData.sector ? { background: "#f5f5f5" } : {}}
              >
                {getSectorOptions(selectedMacro || form.getFieldValue('macroeconomicSector')).map((val) => (
                  <Option key={val} value={val}>{val}</Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item 
            name="industry" 
            label="Industry"
          >
            {(addLevel === 'macro' || addLevel === 'sector' || addLevel === 'industry' || !addLevel) ? (
              <Input 
                placeholder="Enter Industry"
                disabled={parentData && parentData.industry}
                style={parentData && parentData.industry ? { background: "#f5f5f5" } : {}}
              />
            ) : (
              <Select
                placeholder="Select Industry"
                showSearch
                allowClear
                disabled={parentData && parentData.industry}
                style={parentData && parentData.industry ? { background: "#f5f5f5" } : {}}
              >
                {getIndustryOptions(
                  selectedMacro || form.getFieldValue('macroeconomicSector'),
                  selectedSector || form.getFieldValue('sector')
                ).map((val) => (
                  <Option key={val} value={val}>{val}</Option>
                ))}
              </Select>
            )}
          </Form.Item>

          <Form.Item 
            name="basicIndustry" 
            label="Basic Industry"
          >
            <Input 
              placeholder="Enter Basic Industry"
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Add notes or description..." />
          </Form.Item>
          
          {parentData && (
            <div style={{ 
              padding: "12px", 
              background: "#e6f7ff", 
              borderRadius: "8px",
              border: "1px solid #91d5ff",
              marginTop: "16px"
            }}>
              <Text type="secondary">
                <strong>Note:</strong> Parent fields are locked. You're adding a child item under the selected parent.
              </Text>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SectorIndustryClassificationManagement;

















